<template>
  <div class="rj-page-card">
    <div class="rj-page-card__title">服务产品信息</div>
    <div class="rj-page-card__content">
      <div v-if="!disabled" class="storage-toolbar">
        <el-button type="primary" @click="openPicker">
          添加产品
        </el-button>
        <el-autocomplete v-model="storageName" :fetch-suggestions="queryStorageName" placeholder="请输入库位" />
        <el-button type="primary" @click="setAllStorage">批量设置库位</el-button>
      </div>
      <el-table :data="products" border show-summary :summary-method="getSummaries" empty-text="原合同没有可退服务产品">
        <el-table-column type="index" label="序号" width="70" />
        <!-- 产品清单来自数仓，同一客户 PO 多订单时相同产品会各占一行，用 ERP 订单号区分 -->
        <el-table-column prop="erpOrderNum" label="ERP订单号" min-width="130" />
        <el-table-column prop="erpLineNum" label="ERP行号" min-width="100" />
        <el-table-column prop="productCode" label="销售产品代码" min-width="150" />
        <el-table-column prop="materialNum" label="料号" min-width="130" />
        <el-table-column prop="productName" label="产品型号" min-width="180" />
        <el-table-column prop="productUnit" label="单位" width="90" />
        <el-table-column prop="serviceLife" label="服务年限" width="100" />
        <el-table-column prop="productNum" label="数量" width="130">
          <template slot-scope="scope">
            <!-- 销售借用纯服务退货按整数退货（用户当前决策 2026-10-06）：
                 数量可编辑用于部分退货，但不允许小数；小数精度能力仅适用于
                 服务独立销售订单（specs/021），017 明确不在其范围内 -->
            <el-input-number
              v-if="!disabled"
              v-model="scope.row.productNum"
              :min="1"
              :max="Number(scope.row.originalQuantity) || Infinity"
              :precision="0"
              :step="1"
              :step-strictly="true"
              :controls="false"
              size="mini"
              class="num-input"
              @change="(cur, old) => onQuantityChange(scope.row, cur, old)"
            />
            <span v-else>-{{ scope.row.productNum }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="productPrice" label="单价" min-width="120" align="right">
          <template slot-scope="scope">{{ formatAmount(scope.row.productPrice) }}</template>
        </el-table-column>
        <el-table-column prop="productPriceAdd" label="订单金额" min-width="130" align="right">
          <template slot-scope="scope">{{ formatAmount(scaledAmount(scope.row.productPriceAdd, scope.row)) }}</template>
        </el-table-column>
        <el-table-column prop="productActualPriceAdd" label="实际结算金额" min-width="140" align="right">
          <template slot-scope="scope">{{ formatAmount(scaledAmount(scope.row.productActualPriceAdd, scope.row)) }}</template>
        </el-table-column>
        <el-table-column prop="beforeSplitPrice" label="拆分前单价" min-width="130" align="right">
          <template slot-scope="scope">{{ formatAmount(scope.row.beforeSplitPrice) }}</template>
        </el-table-column>
        <el-table-column prop="fictitiousReturn" label="真实/虚拟退货" min-width="130">
          <template slot-scope="scope">{{ scope.row.fictitiousReturn === false ? '虚拟退' : '真实退' }}</template>
        </el-table-column>
        <el-table-column prop="storageName" min-width="180">
          <!-- 库位必填范围对齐既有退货合同：仅已发货行标红星，未发货行可留空 -->
          <template slot="header">
            <span v-if="hasDeliveredRow" class="required-mark">*</span> 库位
          </template>
          <template slot-scope="scope">
            <el-autocomplete v-if="!disabled" v-model="scope.row.storageName" :fetch-suggestions="queryStorageName" placeholder="请输入库位" />
            <span v-else>{{ scope.row.storageName || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="taxRate" label="税率" min-width="120" />
        <el-table-column v-if="!disabled" label="操作" width="80" fixed="right">
          <template slot-scope="scope"><el-button type="text" @click="$emit('remove', scope.$index)">删除</el-button></template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 添加产品：从数仓返回的全部可退服务行中挑选，已添加的行不可重复选 -->
    <el-dialog title="添加产品" :visible.sync="pickerVisible" width="900px" append-to-body>
      <el-table
        ref="pickerTable"
        v-loading="pickerLoading"
        :data="pickerRows"
        border
        height="380"
        row-key="erpLineId"
        empty-text="没有可添加的服务产品"
        @selection-change="onPickerChange"
      >
        <el-table-column type="selection" width="55" :selectable="isSelectable" />
        <el-table-column prop="erpOrderNum" label="ERP订单号" min-width="130" />
        <el-table-column prop="erpLineNum" label="ERP行号" min-width="100" />
        <el-table-column prop="productCode" label="销售产品代码" min-width="150" />
        <el-table-column prop="materialNum" label="料号" min-width="130" />
        <el-table-column prop="productName" label="产品型号" min-width="180" />
        <el-table-column prop="productUnit" label="单位" width="80" />
        <el-table-column prop="productNum" label="数量" width="80">
          <template slot-scope="scope">-{{ scope.row.productNum }}</template>
        </el-table-column>
        <el-table-column prop="productPrice" label="单价" min-width="110" align="right">
          <template slot-scope="scope">{{ formatAmount(scope.row.productPrice) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="90">
          <template slot-scope="scope">
            <span v-if="isAdded(scope.row)" class="picker-added">已添加</span>
            <span v-else>-</span>
          </template>
        </el-table-column>
      </el-table>
      <div slot="footer" class="picker-footer">
        <el-button @click="pickerVisible = false">取消</el-button>
        <el-button type="primary" :disabled="!pickerSelection.length" @click="confirmPicker">
          添加（{{ pickerSelection.length }}）
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { MathAdd, MathMulti } from '@/libs/util';
import { getStorageByKeyWords } from '@/API';

export default {
  name: 'SalesBorrowProduct',
  data () {
    return {
      storageName: '',
      // 添加产品弹窗：候选行来自父页面的全量可退产品
      pickerVisible: false,
      pickerRows: [],
      pickerSelection: []
    };
  },
  props: {
    products: { type: Array, default: () => [] },
    // 数仓返回的全部可退服务行，用于「添加产品」候选
    availableProducts: { type: Array, default: () => [] },
    disabled: Boolean
  },
  computed: {
    /**
     * 当前产品行中是否存在已发货行。
     *
     * 库位必填范围对齐既有退货合同——只有已发货行必须填写库位，
     * 未发货行允许留空，因此仅当存在已发货行时才展示必填星号。
     */
    hasDeliveredRow () {
      return this.products.some(item => Number(item.deliverSign) === 1);
    }
  },
  methods: {
    /**
     * 打开「添加产品」弹窗。
     *
     * 候选集为父页面拉取的全部可退服务行；已加入当前申请的行仍展示，
     * 但选择框置灰并标记「已添加」，避免重复添加。
     */
    openPicker () {
      this.pickerSelection = [];
      this.pickerRows = this.availableProducts.slice();
      this.pickerVisible = true;
      this.$nextTick(() => {
        const table = this.$refs.pickerTable;
        if (table) table.clearSelection();
      });
    },
    /**
     * 判断候选行是否已加入当前申请。
     *
     * @param {object} row 候选行
     * @returns {boolean} 是否已添加
     */
    isAdded (row) {
      const lineId = String(row.erpLineId);
      return this.products.some(item => String(item.erpLineId) === lineId);
    },
    /**
     * 候选行是否可选：已添加的行禁用选择。
     *
     * @param {object} row 候选行
     * @returns {boolean} 是否可选
     */
    isSelectable (row) {
      return !this.isAdded(row);
    },
    onPickerChange (selection) {
      this.pickerSelection = selection;
    },
    /**
     * 确认添加选中的产品行。
     *
     * 新增行不含库位，需用户自行填写（提交时会校验库位必填）。
     */
    confirmPicker () {
      this.pickerSelection.forEach(row => {
        if (!this.isAdded(row)) this.$emit('add', { ...row });
      });
      this.pickerVisible = false;
    },
    /**
     * 格式化金额展示值。
     *
     * <p>
     * 产品金额来自数仓（GetOrderLine），源字段为高精度小数：
     * AFTER_SPLIT_PRICE / BEFORE_SPLIT_AMOUNT / ACTUAL_SETTLEMENT_AMOUNT /
     * BEFORE_SPLIT_PRICE 实测可达 6 位（如 7569.280496、153.544444），
     * 直接渲染会出现长尾小数。
     * </p>
     * <p>
     * 页面按金额口径统一保留 2 位小数展示，与原型明细行/合计行以及
     * 既有退货合同合计行（Number(...).toFixed(2)）保持一致。
     * 仅影响展示，不改动落库值与抛单报文。
     * </p>
     *
     * @param {number|string} value 原始金额
     * @returns {string} 两位小数金额，空值返回 '-'
     */
    formatAmount (value) {
      if (value === null || value === undefined || value === '') return '-';
      const amount = Number(value);
      return Number.isNaN(amount) ? '-' : amount.toFixed(2);
    },
    queryStorageName (value, provideSuggestions) {
      if (!value) return provideSuggestions([]);
      getStorageByKeyWords({ keyWords: value }).then(response => {
        if (response.data.status !== '200') return provideSuggestions([]);
        provideSuggestions(JSON.parse(response.data.data).map(item => ({ value: item.storageName })));
      }).catch(() => provideSuggestions([]));
    },
    setAllStorage () {
      if (!this.storageName) return;
      this.products.forEach(item => { item.storageName = this.storageName; });
    },
    getSummaries ({ columns, data }) {
      return columns.map((column, index) => {
        if (index === 0) return '合计';
        if (column.property === 'productNum') {
          const total = data.reduce((value, item) => MathAdd(value, item.productNum || 0), 0);
          // 与页面既有口径保持一致：退货数量与金额均以负数呈现
          return `-${Number(total).toFixed(2)}`;
        }
        if (column.property === 'productPriceAdd') {
          const total = data.reduce((value, item) => MathAdd(value, this.scaledAmount(item.productPriceAdd, item)), 0);
          return `-${Number(total).toFixed(2)}`;
        }
        if (column.property === 'productActualPriceAdd') {
          const total = data.reduce((value, item) => MathAdd(value, this.scaledAmount(item.productActualPriceAdd, item)), 0);
          return `-${Number(total).toFixed(2)}`;
        }
        if (column.property === 'beforeSplitPrice') {
          const total = data.reduce((value, item) => MathAdd(value,
            MathMulti(item.beforeSplitPrice || 0, item.productNum || 0)), 0);
          return `-${Number(total).toFixed(2)}`;
        }
        return '';
      });
    },
    /**
     * 修改退货数量后刷新本行展示。
     *
     * <p>
     * 行内的订单金额/实际结算金额始终保存数仓返回的「整行值」，
     * 换算只发生在展示层（{@link scaledAmount}），避免反复编辑时出现重复缩放。
     * </p>
     *
     * @param {object} row 产品行
     * @param {number} current 修改后的退货数量
     * @param {number} old 修改前的退货数量
     */
    onQuantityChange (row, current, old) {
      // 数量非法时回退到修改前的值，保证提交数据始终合法；
      // 展示由 v-model 触发响应式刷新，无需再改写行内金额。
      // 销售借用纯服务退货按整数退货（用户当前决策 2026-10-06）：
      // 小数（如 1.5）与非正数一律视为非法并回退。
      const original = Number(row.originalQuantity) || 0;
      const value = Number(current) || 0;
      if (!Number.isInteger(value) || value <= 0 || (original && value > original)) {
        this.$nextTick(() => { row.productNum = old; });
      }
    },
    /**
     * 按当前退货数量换算整行金额，仅用于展示。
     *
     * <p>
     * 数仓返回的 {@code BEFORE_SPLIT_AMOUNT} 与 {@code ACTUAL_SETTLEMENT_AMOUNT}
     * 都是「整行合计」值（实测满足单价 × 数量），退货数量改动后按比例显示；
     * 数量等于原单数量（或缺少基数）时原样返回，保证未改动行的展示与既有行为一致。
     * </p>
     * <p>
     * 落库换算以后端为准（后端按同一比例缩放并保留两位小数），
     * 前端只回传退货数量，不参与金额计算，避免两侧口径不一致。
     * </p>
     * <p>
     * 数量精度（用户当前决策 2026-10-06）：本流程按整数退货，
     * 因此这里的比例运算仍使用统一公式，整数比例同样适用。
     * </p>
     *
     * @param {number|string} amount 数仓返回的整行金额
     * @param {object} row 产品行
     * @returns {number} 换算后的金额
     */
    scaledAmount (amount, row) {
      const value = Number(amount) || 0;
      const original = Number(row.originalQuantity) || 0;
      const current = Number(row.productNum) || 0;
      if (!original || !current || original === current) return value;
      return Number((value * current / original).toFixed(2));
    },
    sum (data, property) {
      const total = data.reduce((value, item) => MathAdd(value, item[property] || 0), 0);
      return `-${Number(total).toFixed(2)}`;
    }
  }
};
</script>

<style lang="scss" scoped>
.storage-toolbar {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}
.required-mark { color: #f56c6c; }
.picker-added { color: #909399; font-size: 12px; }
.picker-footer { text-align: right; }
/* 数量输入框：与表格单元格贴合，去掉默认宽度造成的换行 */
.num-input { width: 100%; }
.num-input ::v-deep .el-input__inner { text-align: left; }
</style>
